import { buildTaskKey } from './taskKeys';
import {
  TRUNCATION_PREDICTION_STRATEGIES,
  getPredictionStrategyLabel,
  predictionStrategyDisablesParallel,
} from '@/config/runtimeStrategyOptions';

export const DEFAULT_INSTANCES = 1;

const VLLM_TASK_TYPES = new Set(['Summarization', 'ContentGeneration']);

/** vLLM tasks (summarization / content generation) have no Triton instance groups, chunking, or aggregation. */
export function isVllmTaskType(training) {
  return VLLM_TASK_TYPES.has(training?.task_type);
}

// Entity-recognition task types. The "Prediction strategy" (chunk aggregation
// / truncation) is classification-only, so it's hidden + dropped for NER.
const NER_TASK_TYPES = new Set(['NERResponse', 'EntityRecognitionResponse']);

/** Entity-recognition tasks (NER + NER-with-relations). */
export function isNerTaskType(training) {
  return NER_TASK_TYPES.has(training?.task_type);
}

/**
 * Mirrors ClassificationTritonModel: long_text_model = parallel || isLongText,
 * unless the operator picked a truncation prediction strategy, which forces a
 * single-chunk pipeline regardless of input length.
 */
export function taskUsesChunkedAggregation(training, runtimeConfig) {
  if (predictionStrategyDisablesParallel(runtimeConfig?.prediction_strategy)) {
    return false;
  }
  const isLongText = Boolean(
    training?.analysis_data?.is_long_text || training?.is_long_text
  );
  return runtimeConfig?.long_input_processing === 'parallel' || isLongText;
}

/** Omit fields irrelevant to the task's serving stack from API payloads. */
export function sanitizeRuntimeConfigForApi(training, runtimeConfig) {
  const config = { ...runtimeConfig };
  if (isVllmTaskType(training)) {
    config.long_input_processing = null;
    config.prediction_strategy = null;
    return config;
  }
  // NER ignores prediction strategy (chunk aggregation / truncation); never
  // send one so the saved config + file name stay clean.
  if (isNerTaskType(training)) {
    config.prediction_strategy = null;
    return config;
  }
  // Truncation strategies always run single-chunk at inference time
  // (``ClassificationTritonModel`` ignores parallel mode when
  // ``prediction_strategy`` is ``truncate_*``), but we preserve the
  // operator's ``long_input_processing`` choice here so the UI, docker
  // image name, and saved deployment config stay aligned.
  if (predictionStrategyDisablesParallel(config.prediction_strategy)) {
    return config;
  }
  const aggregationStrategy = config.prediction_strategy;
  if (aggregationStrategy === 'mean' || aggregationStrategy === 'max') {
    return config;
  }
  if (!taskUsesChunkedAggregation(training, config)) {
    config.prediction_strategy = null;
  }
  return config;
}

export { TRUNCATION_PREDICTION_STRATEGIES };

/**
 * Prediction strategy recorded on the training artifact — **not** the
 * operator's saved ``preferred_deployment_config``. Used to warn when
 * deployment overrides what the checkpoint was actually trained with.
 *
 * Precedence mirrors how training persists runtime knobs:
 *   1. ``training.prediction_strategy`` — explicit Train-page choice.
 *   2. ``training.inference_args.aggregation_strategy`` or
 *      ``training.optuna_best_params.aggregation_strategy`` — ``mean`` /
 *      ``max`` chosen by Optuna on long-text data.
 *   3. ``training.optuna_best_params.truncation_side`` — ``left`` /
 *      ``right`` from Optuna medium-text routing.
 *   4. ``truncate_end`` — implicit platform default for short-text /
 *      single-chunk runs (``RegularTextProcessor`` with
 *      ``truncation: true`` and HuggingFace's default ``right`` side).
 *
 * Never returns ``null`` for Triton classification models; callers that
 * need to hide the field (e.g. summarization) should gate on task type.
 */
export function getTrainedPredictionStrategy(training) {
  const explicit = training?.prediction_strategy;
  if (explicit) {
    return explicit;
  }

  const aggregation =
    training?.inference_args?.aggregation_strategy ||
    training?.optuna_best_params?.aggregation_strategy;
  if (aggregation === 'mean' || aggregation === 'max') {
    return aggregation;
  }

  const truncationSide = training?.optuna_best_params?.truncation_side;
  if (truncationSide === 'left') {
    return 'truncate_start';
  }
  if (truncationSide === 'right') {
    return 'truncate_end';
  }

  return 'truncate_end';
}

/** User-facing label for the strategy this model version was trained with. */
export function getTrainedPredictionStrategyDisplayLabel(training) {
  return getPredictionStrategyLabel(getTrainedPredictionStrategy(training));
}

/**
 * Per-task runtime defaults (instances / long-input / prediction strategy).
 *
 * Default precedence for ``prediction_strategy``:
 *   1. ``preferred_deployment_config.prediction_strategy`` (operator's last
 *      saved choice for this usecase).
 *   2. ``training.inference_args.aggregation_strategy`` — populated when
 *      the model was trained with ``mean`` / ``max``.
 *   3. ``training.prediction_strategy`` — the top-level observability
 *      field that mirrors the Train page Advanced choice. This is the
 *      only place ``truncate_start`` / ``truncate_end`` survive at the
 *      record level (their runtime side-effect lives on the saved
 *      tokenizer, not in ``inference_args``).
 *   4. Hard fallback to ``truncate_end``. Trainings done with
 *      "Automatic" leave both signals empty (the search space carries
 *      no ``aggregation_strategy`` for short-text data and no
 *      ``truncation_side`` either) so a strategy that always works —
 *      single-chunk truncation from the end — is the safest default.
 *
 * When the resulting ``prediction_strategy`` is a truncation strategy we
 * also force ``long_input_processing`` to ``serial`` because truncation
 * always produces a single chunk.
 */
export function createTaskRuntimeConfig(training, usecase = null) {
  const preferredConfig =
    usecase?.preferred_deployment_config ||
    training?.usecase_preferred_deployment_config ||
    null;
  const isLongText = Boolean(
    training?.analysis_data?.is_long_text || training?.is_long_text
  );
  const trainedAggregationStrategy =
    training?.inference_args?.aggregation_strategy || null;
  const trainedPredictionStrategy = training?.prediction_strategy || null;

  // NER has no prediction strategy (the control is hidden for it).
  const predictionStrategy = isNerTaskType(training)
    ? null
    : preferredConfig?.prediction_strategy ||
      trainedAggregationStrategy ||
      trainedPredictionStrategy ||
      'truncate_end';

  let longInputProcessing =
    preferredConfig?.long_input_processing ||
    (isLongText ? 'parallel' : 'serial');
  if (predictionStrategyDisablesParallel(predictionStrategy)) {
    longInputProcessing = 'serial';
  }

  return {
    instance_group_count:
      preferredConfig?.instance_group_count ?? DEFAULT_INSTANCES,
    long_input_processing: longInputProcessing,
    prediction_strategy: predictionStrategy,
  };
}

export function createSelectedTask(training, usecase = null) {
  const runtimeConfig = createTaskRuntimeConfig(training, usecase);
  return {
    taskKey: buildTaskKey(training),
    training,
    runtimeConfig,
  };
}
